import useCallbackRef from 'hooks/useCallbackRef';
import useMatchMedia from 'hooks/useMatchMedia';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { MoveModal } from './components/MoveModal';
import { MoveDrawer } from './components/MoveDrawer';
import { MoveFilesToFolderProps } from './components/MoveFilesToFolder/MoveFilesToFolder.component';

type MoveDocumentProps = Pick<MoveFilesToFolderProps, 'nodes'>;

export type MoveDocumentRef = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

const MoveDocument: React.ForwardRefRenderFunction<
  MoveDocumentRef,
  MoveDocumentProps
> = (props, ref) => {
  const { documentId } = useParams<{ documentId: string }>();

  const isPhablet = useMatchMedia('isPhablet');

  const [nodeToMoveTo, setNodeToMoveTo] = useState<string | null>(documentId);

  const [moveTheFileModal, moveTheFileModalRef] =
    useCallbackRef<ModalRef | null>(null);

  const [moveTheFileDrawer, moveTheFileDrawerRef] =
    useCallbackRef<SideDrawerRef | null>(null);

  const isOpen = useMemo(
    () => moveTheFileDrawer?.open || moveTheFileModal?.openState,
    [moveTheFileDrawer?.open, moveTheFileModal?.openState],
  );

  const open = useCallback(
    () =>
      isPhablet ? moveTheFileModal?.open() : moveTheFileDrawer.setOpen(true),
    [isPhablet, moveTheFileDrawer, moveTheFileModal],
  );

  const close = useCallback(
    () =>
      isPhablet ? moveTheFileModal?.close() : moveTheFileDrawer.setOpen(false),
    [isPhablet, moveTheFileDrawer, moveTheFileModal],
  );

  useImperativeHandle(
    ref,
    () => ({
      isOpen,
      open,
      close,
    }),
    [close, isOpen, open],
  );

  const closeMoveFiles = useCallback(
    () => setNodeToMoveTo(documentId),
    [documentId],
  );

  useEffect(() => {
    setNodeToMoveTo(documentId);
  }, [documentId]);

  if (isPhablet)
    return (
      <MoveModal
        nodeToMoveTo={nodeToMoveTo}
        setNodeToMoveTo={setNodeToMoveTo}
        ref={moveTheFileModalRef}
        closeMoveFiles={closeMoveFiles}
        {...props}
      />
    );

  return (
    <MoveDrawer
      nodeToMoveTo={nodeToMoveTo}
      setNodeToMoveTo={setNodeToMoveTo}
      ref={moveTheFileDrawerRef}
      closeMoveFiles={closeMoveFiles}
      {...props}
    />
  );
};

export default forwardRef(MoveDocument);
